import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";
import confetti from "canvas-confetti";
import { useGame } from "../GameState";
import { SFX, resumeAudio } from "../sfx";
import HUD, { AchievementPopup } from "../components/HUD";
import { GALLERY_PHOTOS } from "../photoManifest";
import { SCRATCH_CARDS, SCRATCH_HEADER, SCRATCH_SUB } from "../MothersDayData";

const REVEAL_THRESHOLD = 0.45;

const fireRevealConfetti = () => {
  const colors = ["#ff6b9d", "#ffd93d", "#c44569", "#ff1493", "#ffa07a"];
  confetti({ particleCount: 55, spread: 70, origin: { y: 0.5 }, colors, scalar: 1.1 });
};

const ScratchAct = ({ onNext }) => {
  const { addScore, addAchievement } = useGame();
  const [phase, setPhase] = useState("ready");
  const [cardIdx, setCardIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [revealPct, setRevealPct] = useState(0);
  const [cardsRevealed, setCardsRevealed] = useState(0);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const card = SCRATCH_CARDS[cardIdx];
  const photo = GALLERY_PHOTOS[card?.photoIndex] || GALLERY_PHOTOS[0];
  const isLastCard = cardIdx >= SCRATCH_CARDS.length - 1;

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.scale(dpr, dpr);
    ctxRef.current = ctx;

    // Draw sparkly gradient overlay
    const grad = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    grad.addColorStop(0, "#ffd1dc");
    grad.addColorStop(0.3, "#ffb6c1");
    grad.addColorStop(0.5, "#ffc0cb");
    grad.addColorStop(0.7, "#ffb6c1");
    grad.addColorStop(1, "#ffd1dc");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Add sparkle dots
    ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
    for (let i = 0; i < 80; i++) {
      const x = Math.random() * rect.width;
      const y = Math.random() * rect.height;
      const r = Math.random() * 3 + 1;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // Add shimmer lines
    ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
    ctx.lineWidth = 1;
    for (let i = 0; i < 12; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * rect.width, 0);
      ctx.lineTo(Math.random() * rect.width, rect.height);
      ctx.stroke();
    }

    // Tease text
    ctx.fillStyle = "rgba(196, 69, 105, 0.85)";
    ctx.font = "italic 600 clamp(14px, 3.5vw, 18px) Georgia, serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const lines = card.tease.split("...");
    const lineHeight = 28;
    const startY = rect.height / 2 - ((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line + (i < lines.length - 1 ? "..." : ""), rect.width / 2, startY + i * lineHeight);
    });

    setRevealed(false);
    setRevealPct(0);
  }, [card]);

  useEffect(() => {
    if (phase === "playing" && !revealed) {
      initCanvas();
    }
  }, [phase, cardIdx, revealed, initCanvas]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const scratch = (pos) => {
    const ctx = ctxRef.current;
    if (!ctx || revealed) return;
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 28, 0, Math.PI * 2);
    ctx.fill();
    // Line from last position for smoother strokes
    ctx.lineWidth = 56;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };

  const checkReveal = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!ctx || !canvas) return;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let transparent = 0;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) transparent++;
    }
    const pct = transparent / (pixels.length / 4);
    setRevealPct(pct);
    if (pct >= REVEAL_THRESHOLD && !revealed) {
      doReveal();
    }
  };

  const doReveal = () => {
    setRevealed(true);
    SFX.win();
    fireRevealConfetti();
    addScore(80);
    const newCount = cardsRevealed + 1;
    setCardsRevealed(newCount);
    if (newCount >= SCRATCH_CARDS.length) {
      addAchievement("scratcher", "Memory Uncoverer", `Revealed all ${SCRATCH_CARDS.length} hidden moments`);
    }
  };

  const handleStart = (e) => {
    e.preventDefault();
    if (revealed) return;
    isDrawing.current = true;
    lastPos.current = getPos(e);
    scratch(lastPos.current);
  };

  const handleMove = (e) => {
    e.preventDefault();
    if (!isDrawing.current || revealed) return;
    const pos = getPos(e);
    scratch(pos);
  };

  const handleEnd = () => {
    isDrawing.current = false;
    checkReveal();
  };

  const nextCard = () => {
    if (isLastCard) {
      setPhase("done");
    } else {
      setCardIdx((i) => i + 1);
      setRevealed(false);
    }
  };

  const start = () => {
    resumeAudio();
    SFX.click();
    setCardIdx(0);
    setRevealed(false);
    setCardsRevealed(0);
    setPhase("playing");
  };

  return (
    <motion.div
      className="md-act md-act-game md-act-scratch"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
    >
      <HUD level={5} levelLabel="Scratch to Reveal" />
      <AchievementPopup />

      <div className="md-act-header">
        <h1>{SCRATCH_HEADER}</h1>
        <div className="md-sub">{cardIdx + 1} / {SCRATCH_CARDS.length} · {SCRATCH_SUB}</div>
      </div>

      {phase === "playing" && (
        <div className="md-scratch-stage">
          <div className="md-scratch-card">
            <div
              className="md-scratch-photo"
              style={{ backgroundImage: `url(${photo})` }}
            />
            <canvas
              ref={canvasRef}
              className={`md-scratch-canvas ${revealed ? "md-scratch-done" : ""}`}
              onMouseDown={handleStart}
              onMouseMove={handleMove}
              onMouseUp={handleEnd}
              onMouseLeave={handleEnd}
              onTouchStart={handleStart}
              onTouchMove={handleMove}
              onTouchEnd={handleEnd}
            />
            {!revealed && (
              <div className="md-scratch-hint">
                <span className="md-scratch-hint-icon">👆</span>
                scratch with your finger
              </div>
            )}
          </div>

          <AnimatePresence>
            {revealed && (
              <motion.div
                className="md-scratch-reveal"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <div className="md-scratch-quote">{card.reveal}</div>
                <div className="md-scratch-from">— Aven</div>
                <button className="md-btn-soft md-cta" onClick={nextCard}>
                  {isLastCard ? "Finish ✓" : "Next memory →"}
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="md-scratch-progress">
            {SCRATCH_CARDS.map((_, i) => (
              <div
                key={i}
                className={`md-scratch-dot ${i < cardsRevealed ? "md-done" : ""} ${i === cardIdx ? "md-active" : ""}`}
              />
            ))}
          </div>
        </div>
      )}

      <AnimatePresence>
        {phase === "ready" && (
          <motion.div className="md-game-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="md-game-card">
              <div className="md-game-badge">Bonus</div>
              <h2>Scratch to Reveal</h2>
              <p className="md-game-tagline">
                Hidden under each sparkly layer is a memory.<br />
                Use your finger to scratch and uncover them.
              </p>
              <ul className="md-game-rules">
                <li>5 hidden photos with messages from Aven</li>
                <li>Scratch away ~50% to reveal</li>
                <li>+80 points per reveal</li>
              </ul>
              <div className="md-game-actions">
                <button className="md-btn-soft md-cta" onClick={start}>Start scratching →</button>
                <button className="md-btn-soft md-btn-ghost" onClick={onNext}>Skip</button>
              </div>
            </div>
          </motion.div>
        )}

        {phase === "done" && (
          <motion.div className="md-game-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="md-game-card md-game-win">
              <div className="md-game-badge md-badge-gold">+{cardsRevealed * 80}</div>
              <h2>All memories uncovered</h2>
              <p className="md-game-aven-says">
                'Mama, every one of those moments lives inside me. Forever.'
              </p>
              <button className="md-btn-soft md-cta" onClick={onNext}>Continue →</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ScratchAct;
